/**
 * `/inbox/threads/:id` — one operator-mail MATTER (operator-inbox slice 5).
 *
 * Three regions, per the plan:
 *
 *   (a) PERSON — the same `InlineFieldEditor` grid `/pr/people/:id` renders, on
 *       LITERALLY the same `outreach_targets` row (a note added here shows on
 *       the PR board and vice versa). Comes from `GET /api/outreach/:id`.
 *       A MEMBER matter has no such row (migration 157) and links to
 *       `/members/:id` instead — see the exactly-one-person CHECK.
 *   (b) THREAD OVERVIEW — the genuinely new widget: a stage/urgency headline,
 *       *waiting on* DERIVED from the stage (it cannot go stale), where it
 *       landed, the classification + rationale, what we owe (open items = the
 *       unmatched-commitment set), the revision counter, and the first-arrival
 *       progress strip. From `GET /api/inbox/threads/:id`, plus the full
 *       timeline (with Mario's verbatim feedback rounds once slice 7 lands).
 *   (c) CONVERSATION — the SHARED `ConversationView`, fed by the outreach
 *       payload's messages/targets/tracking. Zero component changes: it keys
 *       on plain address strings, never a member id.
 *
 * Two fetches, composed client-side — the person/conversation SQL exists once
 * (outreach-detail.ts) and the overview SQL once (inbox-board.ts).
 *
 * MATTER LIFECYCLE (2026-08-07): the page is no longer read-only for the
 * matter. Close / escalate / return-to-triage / log-event / feedback /
 * clear-latch and the inline matter-field grid all dispatch the SAME canonical
 * actions the inbox agent, the drain and the CLI use — one write path onto
 * `inbox_threads` (invariant 5). Two omissions are deliberate and load-bearing:
 * the Log-event modal offers NO `stage` (the action's enum is all nine stages
 * with no advance guard, so a note could stamp `replied_at` for a reply that
 * never left), and the field grid offers NO `next_action_at` (a blank inline
 * draft would send `null`, clearing the agent's due cursor and stalling the
 * matter silently until the 48h drift report).
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  api,
  ApiError,
  type InboxThreadDetailResponse,
  type MemberConversationResponse,
  type OutreachDetailResponse,
} from "../lib/api";
import { studioFormat } from "../lib/studio-tz";
import { PR_EDITABLE_FIELDS } from "../lib/pr-person-edit";
import {
  INBOX_LOG_EVENT_KINDS,
  INBOX_MATTER_EDITABLE_FIELDS,
  validateInboxEventDraft,
} from "../lib/inbox-matter-edit";
import { InlineFieldEditor } from "../components/InlineFieldEditor";
import { ActionModalButton } from "../components/ActionModalButton";
import {
  ConversationView,
  deriveEmailReplySubject,
} from "../components/ConversationView";
import { inboxStageLabel } from "../lib/inbox-board";

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function fmtDateTime(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATETIME_OPTS).format(d);
}

/**
 * Whose move is it? DERIVED from the stage, never stored — the two-timestamp
 * derivation the plan asked for, so it cannot drift from the board.
 */
function waitingOn(stage: string): { label: string; tone: string } {
  switch (stage) {
    case "awaiting_mario":
      return { label: "Waiting on YOU — a draft needs approval", tone: "tag-error" };
    case "needs_mario":
      return { label: "Needs YOU — escalated, no auto-draft", tone: "tag-error" };
    case "unclassified":
    case "triaged":
    case "drafting":
    case "revising":
      return { label: "Waiting on the assistant", tone: "" };
    case "replied":
    case "awaiting_them":
      return { label: "Waiting on them", tone: "tag-success" };
    case "closed":
      return { label: "Closed", tone: "" };
    default:
      return { label: stage, tone: "" };
  }
}

const KIND_ICONS: Readonly<Record<string, string>> = {
  opened: "📥",
  received: "📨",
  classified: "🏷️",
  notified: "🔔",
  draft_staged: "📝",
  feedback: "🗣️",
  reply_sent: "📤",
  commitment: "📌",
  commitment_done: "✅",
  escalated: "⚠️",
  closed: "🏁",
  note: "•",
};

export function InboxThreadDetail(): JSX.Element {
  const { id = "" } = useParams();
  const [detail, setDetail] = useState<InboxThreadDetailResponse | null>(null);
  const [person, setPerson] = useState<OutreachDetailResponse | null>(null);
  const [memberConvo, setMemberConvo] =
    useState<MemberConversationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useMemo(
    () => async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const d = await api.inboxThreadDetail(id);
        setDetail(d);
        // The person half rides the EXISTING outreach endpoint — same row
        // Peter edits. A person-fetch failure degrades to overview-only
        // rather than blanking the page.
        //
        // A MEMBER matter has no outreach_targets row at all (migration 157),
        // so there is nothing to fetch — skip rather than request `/null` and
        // swallow the 404, which is what silently blanked the region before.
        //
        // 2026-08-15 (inbox-pr-simplify) — a MEMBER matter instead fetches
        // the member-page conversation payload, so the matter shows the very
        // emails it is about ("Conversation unavailable." on a matter about
        // an email thread was the live complaint). Same endpoint the member
        // page uses; a failure degrades to the pointer line, never an error.
        const targetId = d.thread.outreach_target_id;
        if (targetId !== null) {
          setMemberConvo(null);
          try {
            setPerson(await api.outreachDetail(targetId));
          } catch {
            setPerson(null);
          }
        } else {
          setPerson(null);
          if (d.thread.member_id !== null) {
            try {
              setMemberConvo(await api.memberConversation(d.thread.member_id));
            } catch {
              setMemberConvo(null);
            }
          } else {
            setMemberConvo(null);
          }
        }
      } catch (e) {
        setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <p style={{ padding: 16 }}>Loading…</p>;
  if (error !== null || detail === null) {
    return (
      <div style={{ padding: 16 }}>
        <p style={{ color: "#c0182f" }}>{error ?? "Not found."}</p>
        <Link to="/inbox">← Back to Inbox</Link>
      </div>
    );
  }

  const t = detail.thread;
  const w = waitingOn(t.stage);
  const c = person?.contact ?? null;

  const editorValue = (field: string): string => {
    if (c === null) return "";
    const v = (c as unknown as Record<string, unknown>)[field];
    if (v === null || v === undefined) return "";
    return String(v);
  };

  /** Same shape for the MATTER row (update_inbox_thread), not the person. */
  const matterValue = (field: string): string => {
    const v = (t as unknown as Record<string, unknown>)[field];
    if (v === null || v === undefined) return "";
    return String(v);
  };

  return (
    <div style={{ padding: 16, maxWidth: 860, margin: "0 auto" }}>
      <Link to="/inbox" style={{ fontSize: "0.85rem" }}>
        ← Back to Inbox
      </Link>

      {/* (b) Thread overview — the headline is the matter, not the person. */}
      <div style={{ margin: "8px 0 16px" }}>
        {/* One status signal — the waitingOn chip below. The raw stage tag
            came off 2026-08-15 (inbox-pr-simplify): two chips saying almost
            the same thing, one of them a nine-value enum, was the confusion. */}
        <h1 style={{ margin: "0 0 4px", fontSize: "1.4rem" }}>
          {t.subject ?? t.summary ?? "Untitled matter"}{" "}
          {t.urgency !== null && t.urgency !== "normal" ? (
            <span
              className={`tag ${t.urgency === "high" ? "tag-error" : ""}`}
            >
              {t.urgency}
            </span>
          ) : null}
        </h1>
        <p style={{ margin: "0 0 8px" }}>
          <span className={`tag ${w.tone}`}>{w.label}</span>{" "}
          <span className="dc-muted" style={{ fontSize: "0.85rem" }}>
            · landed in {t.mailbox_identifier}
            {t.revision_round > 0 ? ` · revision round ${t.revision_round}` : ""}
            {t.notify_pending ? " · 🔔 notification pending" : ""}
          </span>
        </p>

        {t.summary !== null ? (
          <p style={{ margin: "0 0 4px", fontSize: "0.95rem" }}>{t.summary}</p>
        ) : null}
        {t.classifier_rationale !== null ? (
          <p className="dc-muted" style={{ margin: "0 0 8px", fontSize: "0.85rem" }}>
            Classifier: {t.category ?? "—"} — {t.classifier_rationale}
          </p>
        ) : null}
        {t.next_action !== null || t.next_action_at !== null ? (
          <p style={{ margin: "0 0 8px", fontSize: "0.9rem" }}>
            <strong>Next:</strong> {t.next_action ?? "—"}
            {t.next_action_at !== null
              ? ` (by ${fmtDateTime(t.next_action_at)})`
              : ""}
          </p>
        ) : null}
        {t.closed_reason !== null ? (
          <p className="dc-muted" style={{ margin: "0 0 8px", fontSize: "0.9rem" }}>
            Closed: {t.closed_reason}
          </p>
        ) : null}

        {/* Open items — the unmatched-commitment set, "what we owe". Each
            carries its own discharge button: `commitment_done` REQUIRES the
            timeline id it resolves, and prefilling it is the only way that is
            not a hand-typed uuid. */}
        {detail.open_commitments.length > 0 ? (
          <div style={{ margin: "8px 0" }}>
            <strong style={{ fontSize: "0.9rem" }}>We owe:</strong>
            <ul style={{ margin: "4px 0 0 18px", fontSize: "0.9rem" }}>
              {detail.open_commitments.map((o) => (
                <li key={o.id}>
                  {o.summary}{" "}
                  <span className="dc-muted">({fmtDateTime(o.at)})</span>{" "}
                  <ActionModalButton
                    action="record_inbox_event"
                    buttonLabel="Mark done"
                    buttonClassName="row-action-btn"
                    buttonTitle="Record this commitment as discharged"
                    modalTitle="Mark commitment done"
                    description={o.summary}
                    prefill={{
                      inbox_thread_id: t.inbox_thread_id,
                      kind: "commitment_done",
                      resolves_timeline_id: o.id,
                    }}
                    fields={[
                      {
                        kind: "text",
                        name: "summary",
                        label: "What happened",
                        required: true,
                        maxLength: 500,
                        placeholder: "reference only — no verbatim quotes",
                      },
                    ]}
                    submitLabel="Mark done"
                    onDone={() => void load()}
                  />
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* Matter lifecycle. These dispatch the SAME canonical actions the
            inbox agent and the CLI use — there is one write path onto
            inbox_threads (invariant 5). `stage` is deliberately absent from
            the Log-event modal: record_inbox_event's enum carries all nine
            stages with no advance guard, so a free picker would let a note
            stamp `replied_at` for a reply that never left. */}
        <div className="matter-actions">
          {t.stage !== "closed" ? (
            <ActionModalButton
              action="update_inbox_thread"
              buttonLabel="Close matter"
              buttonClassName="row-action-btn row-action-btn-danger"
              buttonTitle="Take this matter off the board"
              modalTitle="Close this matter"
              description="It leaves the open board. The inbox is cyclical, so a new reply can reopen it."
              prefill={{ inbox_thread_id: t.inbox_thread_id, stage: "closed" }}
              fields={[
                {
                  kind: "textarea",
                  name: "closed_reason",
                  label: "Why is this closed?",
                  required: true,
                  maxLength: 500,
                  placeholder: "resolved · dropped · lapsed · …",
                },
              ]}
              submitLabel="Close matter"
              onDone={() => void load()}
            />
          ) : null}

          {t.stage !== "needs_mario" ? (
            <ActionModalButton
              action="record_inbox_event"
              buttonLabel="Escalate to me"
              buttonClassName="row-action-btn"
              buttonTitle="Take this off the assistant and handle it yourself"
              modalTitle="Escalate this matter"
              description="Stops the assistant drafting. You handle it from here."
              prefill={{
                inbox_thread_id: t.inbox_thread_id,
                kind: "escalated",
                stage: "needs_mario",
              }}
              fields={[
                {
                  kind: "text",
                  name: "summary",
                  label: "Why does this need you?",
                  required: true,
                  maxLength: 500,
                },
              ]}
              submitLabel="Escalate"
              onDone={() => void load()}
            />
          ) : null}

          {/* The ONLY sanctioned way back onto the assistant's queue. Without
              it, escalating is a one-way door: update_inbox_thread admits only
              closed|needs_mario, and needs_mario is not in the worker's sweep. */}
          {t.stage === "needs_mario" || t.stage === "closed" ? (
            <ActionModalButton
              action="record_inbox_event"
              buttonLabel="Return to triage"
              buttonClassName="row-action-btn"
              buttonTitle="Hand this back to the assistant"
              modalTitle="Return to the assistant"
              description="Puts the matter back in the assistant's queue, due now."
              prefill={{
                inbox_thread_id: t.inbox_thread_id,
                kind: "note",
                stage: "triaged",
                next_action_at: new Date().toISOString(),
              }}
              fields={[
                {
                  kind: "text",
                  name: "summary",
                  label: "What should it pick up?",
                  required: true,
                  maxLength: 500,
                },
              ]}
              submitLabel="Return to triage"
              onDone={() => void load()}
            />
          ) : null}

          <ActionModalButton
            action="record_inbox_event"
            buttonLabel="Log an event"
            buttonClassName="row-action-btn"
            buttonTitle="Append a line to this matter's timeline"
            modalTitle="Log an event"
            description="Appends to the timeline. Summaries are reference-only — never paste the correspondence."
            prefill={{ inbox_thread_id: t.inbox_thread_id }}
            fields={[
              {
                kind: "select",
                name: "kind",
                label: "Kind",
                required: true,
                defaultValue: "note",
                options: INBOX_LOG_EVENT_KINDS.map((k) => ({
                  value: k,
                  label: k,
                })),
              },
              {
                kind: "textarea",
                name: "summary",
                label: "Summary",
                required: true,
                maxLength: 500,
              },
              { kind: "datetime", name: "at", label: "When (defaults to now)" },
            ]}
            validate={validateInboxEventDraft}
            submitLabel="Log it"
            onDone={() => void load()}
          />

          <ActionModalButton
            action="record_inbox_feedback"
            buttonLabel="Record my feedback"
            buttonClassName="row-action-btn"
            buttonTitle="Tell the assistant what to change, verbatim"
            modalTitle="Feedback to the assistant"
            description="Your words reach the assistant verbatim and it redrafts. Three rounds, then it escalates to you."
            prefill={{ inbox_thread_id: t.inbox_thread_id }}
            fields={[
              {
                kind: "textarea",
                name: "feedback",
                label: "What should change?",
                required: true,
                maxLength: 4000,
              },
            ]}
            submitLabel="Send feedback"
            onDone={() => void load()}
          />

          {t.notify_pending ? (
            <ActionModalButton
              action="update_inbox_thread"
              buttonLabel="Clear notify latch"
              buttonClassName="row-action-btn"
              buttonTitle="Stop the drain re-sending the Telegram notification"
              modalTitle="Clear the notification latch"
              description="The drain leaves this ON until a notification actually sends. Clear it if you have already seen this matter."
              prefill={{
                inbox_thread_id: t.inbox_thread_id,
                notify_pending: false,
              }}
              submitLabel="Clear it"
              onDone={() => void load()}
            />
          ) : null}
        </div>

        {/* Matter fields, inline. Clearing sends an explicit null — '' is
            rejected by the action. `next_action_at` is NOT here on purpose: a
            blank draft would clear the agent's due cursor and stall the matter
            silently. */}
        <div className="matter-field-grid">
          {INBOX_MATTER_EDITABLE_FIELDS.map((s) => (
            <InlineFieldEditor
              key={s.field}
              entityId={t.inbox_thread_id}
              action="update_inbox_thread"
              idField="inbox_thread_id"
              spec={s}
              value={matterValue(s.field)}
              onSaved={() => void load()}
            />
          ))}
        </div>

        {/* 2026-08-15 (inbox-pr-simplify) — the first-arrival stamp strip is
            GONE: the matter's status is the one waitingOn chip above, and the
            timeline below already carries every event with its timestamp. A
            seven-pip pipeline on the operator's own mail was decoration that
            rotted (the eternal "awaiting them" complaint). */}
      </div>

      {/* (a) Person — the SAME editable grid /pr/people/:id renders, on the
          same row. Degrades to a plain line when the person fetch failed. */}
      <div style={{ margin: "0 0 16px" }}>
        <h2 style={{ margin: "0 0 4px", fontSize: "1.05rem" }}>
          {t.target_name}{" "}
          <span className="tag">{t.target_type}</span>{" "}
          {/* Exactly one of these is set (the exactly-one-person CHECK). A
              member matter must link to the MEMBER page — linking it at
              /pr/people/null was a dead link on every member matter. */}
          {t.outreach_target_id !== null ? (
            <Link
              to={`/pr/people/${encodeURIComponent(t.outreach_target_id)}`}
              style={{ fontSize: "0.8rem" }}
            >
              full profile →
            </Link>
          ) : t.member_id !== null ? (
            <Link
              to={`/members/${encodeURIComponent(t.member_id)}`}
              style={{ fontSize: "0.8rem" }}
            >
              member profile →
            </Link>
          ) : null}
        </h2>
        {t.outreach_target_id === null && t.member_id !== null ? (
          <p className="dc-muted" style={{ fontSize: "0.85rem" }}>
            {t.target_email ?? "—"} · this is a member — their conversation and
            details live on the member page.
          </p>
        ) : c !== null ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "4px 16px",
              fontSize: "0.85rem",
            }}
          >
            {PR_EDITABLE_FIELDS.filter(
              (s) =>
                s.kind !== "multiline" &&
                ["name", "email", "phone", "company_outlet", "location"].includes(
                  s.field,
                ),
            ).map((s) => (
              <InlineFieldEditor
                key={s.field}
                entityId={c.id}
                action="update_outreach_target"
                idField="outreach_target_id"
                spec={s}
                value={editorValue(s.field)}
                onSaved={() => void load()}
              />
            ))}
            {PR_EDITABLE_FIELDS.filter((s) => s.field === "staff_notes").map(
              (s) => (
                <InlineFieldEditor
                  key={s.field}
                  entityId={c.id}
                  action="update_outreach_target"
                  idField="outreach_target_id"
                  spec={s}
                  value={editorValue(s.field)}
                  onSaved={() => void load()}
                />
              ),
            )}
          </div>
        ) : (
          <p className="dc-muted" style={{ fontSize: "0.85rem" }}>
            {t.target_email ?? "—"}
          </p>
        )}
      </div>

      {/* Timeline — the append-only log, oldest-first. Feedback rounds render
          VERBATIM and highlighted: they are Mario's own words to the agent. */}
      <div style={{ margin: "0 0 16px" }}>
        <h2 style={{ margin: "0 0 6px", fontSize: "1.05rem" }}>
          Timeline{" "}
          <span className="dc-muted" style={{ fontSize: "0.8rem" }}>
            {detail.timeline.length} events
          </span>
        </h2>
        <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {detail.timeline.map((e) => (
            <li
              key={e.id}
              style={{
                padding: "4px 0",
                borderBottom: "1px solid rgba(128,128,128,0.15)",
                fontSize: "0.88rem",
              }}
            >
              <span title={e.kind}>{KIND_ICONS[e.kind] ?? "•"}</span>{" "}
              <span className="dc-muted">{fmtDateTime(e.at)}</span>{" "}
              <strong>{e.kind}</strong>
              {e.stage !== null ? (
                <span className="tag" style={{ marginLeft: 6 }}>
                  → {inboxStageLabel(e.stage)}
                </span>
              ) : null}{" "}
              — {e.summary}
              {e.detail !== null ? (
                <details style={{ margin: "4px 0 0 22px" }}>
                  <summary
                    className="dc-muted"
                    style={{ cursor: "pointer", fontSize: "0.82rem" }}
                  >
                    Detail
                  </summary>
                  <div
                    style={{
                      marginTop: 4,
                      whiteSpace: "pre-wrap",
                      fontSize: "0.85rem",
                    }}
                  >
                    {e.detail}
                  </div>
                </details>
              ) : null}
              {e.feedback !== null ? (
                <blockquote
                  style={{
                    margin: "4px 0 0 22px",
                    padding: "4px 8px",
                    borderLeft: "3px solid #f59e0b",
                    fontStyle: "italic",
                  }}
                >
                  {e.feedback}
                </blockquote>
              ) : null}
            </li>
          ))}
        </ol>
      </div>

      {/* (c) Conversation — the shared component, zero changes. No handover
          slot: there is no AI auto-responder on the operator's own mail.
          2026-08-15 (inbox-pr-simplify) — a MEMBER matter renders the
          member-page conversation payload here (the emails the matter is
          about, all channels), replying included; the bare "Conversation
          unavailable." remains only when the fetch itself failed. */}
      {person !== null ? (
        <ConversationView
          messages={person.messages}
          phone={person.recipient_phone}
          email={person.recipient_email}
          igsid={person.recipient_igsid}
          emailSubjectDefault={deriveEmailReplySubject(person.messages)}
          onSent={() => void load()}
          emailTracking={person.email_tracking}
        />
      ) : memberConvo !== null ? (
        <ConversationView
          messages={memberConvo.messages}
          phone={memberConvo.recipient_phone}
          email={memberConvo.recipient_email}
          igsid={memberConvo.recipient_igsid}
          emailSubjectDefault={deriveEmailReplySubject(memberConvo.messages)}
          onSent={() => void load()}
        />
      ) : (
        <p className="empty">Conversation unavailable.</p>
      )}
    </div>
  );
}
